import UrgencyLabel from 'LuxKit/Label/UrgencyLabel'
import moment from 'moment'
import { useMemo, useState } from 'react'
import * as React from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import useCountdownTagOptions from 'home/hooks/useCountdownTagOptions'
import { filterNumericFont } from 'lib/theme/themeUtils'

const TabularCountdown = styled.span`
  font-variant-numeric: tabular-nums;
  font-family: ${props => filterNumericFont(props.theme.font.primary.family)};
`

export type TagPlace =
  | 'offer-card'
  | 'offer-main'
  | 'offer-gallery'
  | 'offer-search-tile'
  | 'deals-tile-item'
  | 'offer-summary-card'
  | 'bedbank-offer-card'
  | 'offer-label'
  | 'tour-v2-urgency-tags'
  | 'bedbank-offer-intro'
  | 'property-offer-intro'
  | 'common-labels'
  | 'tour-offer-card'
  | 'offer-carousel-card'
  | 'flight-deal-content'
  | 'cruise-content'
  | 'cruise-deal-hero'
  | 'cruise-urgency-label'
  | 'carousel-card-medium'
  | 'offer-labels'
  | 'lux-plus-offer-label'
  | 'tour-deal-hero'

interface Props {
  className?: string
  overrideShowWithinXDays?: number
  endDate: string
  showHourOnly?: boolean
  isLuxPlusEarlyAccess?: boolean
  prefixLabel?: string
  tagPlace: TagPlace
  fallback?: React.ReactElement
}

function OfferUrgencyCountdownLabel(props: Props) {
  const {
    endDate,
    overrideShowWithinXDays,
    isLuxPlusEarlyAccess = false,
    prefixLabel,
    showHourOnly,
    tagPlace,
    fallback = null,
    ...rest
  } = props
  const intl = useIntl()
  const [now, setNow] = useState<moment.Moment>(moment())
  const luxPlusEnabled = useAppSelector(state => getIsLuxPlusEnabled(state))
  const countdownOptions = useCountdownTagOptions({
    tagPlaceId: tagPlace,
    overrideShowWithinXDays,
  })

  const {
    message,
    timeLeft,
  } = useMemo(() => {
    const end = moment(endDate)

    const leftToTheEnd = moment.duration(end.diff(now))
    const roundedEndDays = Math.floor(leftToTheEnd.asDays())
    const endHours = leftToTheEnd.asHours()

    const baseMessage = prefixLabel
      ? intl.formatMessage(
          {
            id: 'Hd7lxlng-jnYWVWnD8PN',
            defaultMessage: '{prefixLabel} ends in',
            description: 'Countdown label prefix with custom offer type ending soon',
          },
          { prefixLabel },
        )
      : intl.formatMessage({
          id: 'CpMAYzV4ILVNy-G9WpET',
          defaultMessage: 'Ends in',
          description: 'Countdown label prefix for offer ending soon on the offer page',
        })
    const message = isLuxPlusEarlyAccess && luxPlusEnabled
      ? intl.formatMessage({
          id: 'csYl-mguC1CGOGR06COd',
          defaultMessage: 'Priority access ends in',
          description: 'Countdown label prefix for LuxPlus early access period ending',
        })
      : baseMessage

    if (endHours == 0) {
      return {
        message: undefined,
        timeLeft: undefined,
      }
    }

    if (endHours > 24) {
      const daysLeftLabel = intl.formatMessage(
        {
          id: 'BOUhFx3rUiZSMmcMkcDb',
          defaultMessage: '{count, plural, one {# day} other {# days}}',
          description: 'Number of days remaining in countdown urgency label',
        },
        { count: roundedEndDays },
      )

      if (isLuxPlusEarlyAccess && luxPlusEnabled) {
        return {
          message,
          timeLeft: daysLeftLabel,
        }
      }

      if (roundedEndDays <= countdownOptions.showWithinXDays) {
        return {
          message: baseMessage,
          timeLeft: daysLeftLabel,
        }
      }
    }

    if (endHours <= 24 && leftToTheEnd.asSeconds() > 0) {
      let durationMs = leftToTheEnd.asMilliseconds()
      // round up the hour
      if (showHourOnly) durationMs += ((59 * 60) + 59) * 1000

      const totalSeconds = Math.floor(durationMs / 1000)
      const hours = Math.floor(totalSeconds / 3600)
      const minutes = Math.floor((totalSeconds % 3600) / 60)
      const seconds = totalSeconds % 60
      const padTimeUnit = (value: number) => String(value).padStart(2, '0')

      const timeLeftString = showHourOnly
        ? padTimeUnit(hours)
        : `${padTimeUnit(hours)}:${padTimeUnit(minutes)}:${padTimeUnit(seconds)}`

      return {
        message,
        timeLeft: <TabularCountdown suppressHydrationWarning>
          {timeLeftString}
        </TabularCountdown>,
      }
    }

    return {
      message: undefined,
      timeLeft: undefined,
    }
  }, [intl, endDate, now, prefixLabel, isLuxPlusEarlyAccess, luxPlusEnabled, countdownOptions.showWithinXDays, showHourOnly])

  // only update per minute if we only show hr
  const updateInterval = showHourOnly ? 60000 : 1000

  // Only run the interval if message is truthy (countdown is visible)
  React.useEffect(() => {
    if (!message) return
    const id = setInterval(() => setNow(moment()), updateInterval)
    return () => clearInterval(id)
  }, [message, updateInterval])

  if (!message) {
    return fallback ?? null
  }

  return (
    <UrgencyLabel
      {...rest}
      data-tag-place-id={tagPlace}
      className={rest.className}
      variant="speed"
    >
      <FormattedMessage
        id="1JbhO6GUYshDUGtO84SY"
        defaultMessage="{message} {timeLeft}"
        description="Urgency countdown label combining message with remaining time"
        values={{
          message,
          timeLeft,
        }}
      />
    </UrgencyLabel>
  )
}

export default OfferUrgencyCountdownLabel
